/**
 * `<th>` 의 시각적 역할(컬럼 헤더 / 행 헤더 / 패딩 헤더)을 DOM 구조로 판단한다.
 *
 * 왜 `scope` 속성을 쓰지 않는가:
 * transpose 된 표에서는 원본 축 의미를 따라 `scope` 가 내려오므로 시각적 위치와
 * 어긋난다. 즉, 시각적으로는 컬럼 헤더(상단)인데 `scope="row"` 가 붙어 있거나,
 * 시각적 행 헤더(좌측)인데 `scope="col"` 이 붙어 있는 케이스가 존재한다.
 * 헤더 클릭 시 시각적 행/열 단위로 데이터 셀을 선택해야 하므로, `scope` 가
 * 아니라 DOM 위치(같은 `<tr>` 안의 데이터 셀 유무, 데이터 영역과의 가로 관계)로
 * 판단한다.
 *
 * 규칙:
 * - 같은 `<tr>` 에 데이터 셀(`[data-cell-id]`)이 있으면 → 행 헤더
 *   (행에 데이터가 있다는 건 그 행 자체가 데이터 행이고, 이 `<th>` 가 그 행을
 *   라벨링한다는 뜻)
 * - 같은 `<tr>` 에 데이터 셀이 없으면 헤더 전용 행 → 다음 둘 중 하나:
 *   - `<th>` 우측 끝이 표 전체 데이터 영역의 좌측 경계보다 왼쪽이면 → 패딩 헤더
 *     (다중 헤더 행 × 다중 행 헤더 컬럼의 교차 영역 전체를 인지하기 위함)
 *   - 그렇지 않으면 → 컬럼 헤더
 */

export type HeaderRole = "col" | "row" | "padding";

const PADDING_BOUNDARY_TOLERANCE_PX = 0.5;

export const determineHeaderRole = (th: HTMLElement): HeaderRole | null => {
  const tr = th.closest("tr");
  if (!tr) return null;

  if (tr.querySelector("[data-cell-id]")) return "row";

  const table = th.closest("table");
  if (!table) return null;

  const dataCells = table.querySelectorAll<HTMLElement>("[data-cell-id]");
  if (dataCells.length === 0) return "padding";

  let minDataLeft = Infinity;
  for (const cell of dataCells) {
    const left = cell.getBoundingClientRect().left;
    if (left < minDataLeft) minDataLeft = left;
  }

  const thRect = th.getBoundingClientRect();
  if (thRect.right <= minDataLeft + PADDING_BOUNDARY_TOLERANCE_PX) {
    return "padding";
  }

  return "col";
};
